import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { raiseDispute } from "@/lib/bookings.functions";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/bookings/$id/dispute")({
  head: () => ({ meta: [{ title: "Raise a dispute — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: DisputePage,
});

function DisputePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const submit = useServerFn(raiseDispute);
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submit({ data: { bookingId: id, reason, description: description || undefined } });
      toast.success("Dispute raised. Our team will review within 24 hours.");
      navigate({ to: "/dashboard" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not raise dispute");
    } finally { setSubmitting(false); }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-xl flex-1 px-4 py-12 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-bold tracking-tight">Raise a dispute</h1>
        <p className="mt-2 text-muted-foreground">Tell us what went wrong. Payouts are held while we investigate.</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (short summary)</Label>
            <Input id="reason" required maxLength={120} placeholder="e.g. Job not completed as agreed" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">What happened?</Label>
            <Textarea id="description" rows={6} maxLength={4000} placeholder="Include dates, what was agreed, and what didn't happen." value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={submitting || reason.length < 4}>
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting…</> : "Submit dispute"}
          </Button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
